import dayjs from "dayjs";

/**
 * Normalize admin date values (ISO string or react-hook-form { startDate }).
 * @param {string|Date|{ startDate?: string|Date|null }|null|undefined} value
 * @returns {string|null} YYYY-MM-DD or null
 */
export function normalizeAdminDateOnly(value) {
  if (value == null) return null;
  const raw =
    typeof value === "object" && value !== null && "startDate" in value
      ? value.startDate
      : value;
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

/**
 * @param {string|Date|{ startDate?: string|Date|null }|null|undefined} originalDate
 * @param {string|Date|{ startDate?: string|Date|null }|null|undefined} formRequestDate
 */
export function hasRequestedDraftDateChanged(originalDate, formRequestDate) {
  return (
    normalizeAdminDateOnly(originalDate) !==
    normalizeAdminDateOnly(formRequestDate)
  );
}

/**
 * @param {{ originalDate?: string|null, formRequestDate?: object|null, changeReason?: string|null }} params
 * @returns {string|null} validation message or null
 */
export function validateRequestedDraftDateChange({
  originalDate,
  formRequestDate,
  changeReason,
}) {
  if (!hasRequestedDraftDateChanged(originalDate, formRequestDate)) {
    return null;
  }
  if (!String(changeReason ?? "").trim()) {
    return "Reason for Change is required when updating the Requested Draft Report Date.";
  }
  return null;
}

/**
 * Form value for the date picker. Null when the saved date is empty.
 * @param {string|Date|{ startDate?: string|Date|null }|null|undefined} savedDate
 * @returns {{ startDate: string, endDate: string }|null}
 */
export function toRequestedDraftDateFormValue(savedDate) {
  const day = normalizeAdminDateOnly(savedDate);
  if (!day) return null;
  return { startDate: day, endDate: day };
}

/**
 * Seed requestDate from the saved value the first time edit mode opens.
 * A later run (details refetch while the user is still editing) must not
 * wipe an in-progress date or reason — currentFormDate is undefined only
 * before the field has been written.
 */
export function applyRequestedDraftDateOnEditEnter(
  setValue,
  savedDate,
  currentFormDate
) {
  if (typeof setValue !== "function") return;
  if (currentFormDate !== undefined) return;

  const seeded = toRequestedDraftDateFormValue(savedDate);
  if (seeded) {
    setValue("requestDate", seeded, { shouldValidate: false });
  }
  setValue("requestedDraftDateChangeReason", "", { shouldValidate: false });
}
